import { useState } from "react";
import axios from "axios";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ArrowUpRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PEOPLE, getVerifiedSolutions } from "@/data/mockPeople";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXAMPLE_PROMPTS = [
  "Our support team is drowning in tickets — need an AI that deflects the easy ones",
  "We spend $18k/month on LLM calls, want to cut cost without losing quality",
  "Publish 2 SEO posts a week without hiring a writer",
];

const leanCandidate = (p) => ({
  id: p.id,
  name: p.name,
  role: p.role,
  bio: p.bio,
  categories: p.categories || [],
  tools: (p.tools || []).map((t) => t.name),
  problems: p.problems.map((pr) => ({
    title: pr.title,
    description: pr.description,
    outcome: pr.outcome || "",
  })),
});

export default function AIMatchDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim()) {
      toast.error("Describe your problem first.");
      return;
    }
    setLoading(true);
    setMatches(null);
    try {
      const res = await axios.post(`${API}/match`, {
        query: query.trim(),
        candidates: PEOPLE.map(leanCandidate),
      });
      const list = (res.data?.matches || [])
        .map((m) => ({
          ...m,
          person: PEOPLE.find((p) => p.id === m.personId),
        }))
        .filter((m) => m.person);
      setMatches(list);
      if (list.length === 0) {
        toast.info("No strong matches — try rephrasing your problem.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong.";
      toast.error(`Match failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setQuery("");
    setMatches(null);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-white border-l border-[#F3F4F6] p-8 overflow-y-auto"
      >
        <SheetHeader className="text-left">
          <div className="w-10 h-10 rounded-2xl bg-[#D6FFEB] text-[#3FFFB6] flex items-center justify-center mb-3">
            <Sparkles size={18} />
          </div>
          <SheetTitle className="font-display text-2xl text-[#111827]">
            Describe your problem
          </SheetTitle>
          <SheetDescription className="text-[#4B5563] font-body">
            Tell us what you're trying to solve in plain English. We'll match you
            with the 3 solvers most likely to help.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-6">
          <Textarea
            data-testid="ai-match-query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. Our customer support team is drowning in tickets. We want an AI that can handle repetitive questions without hallucinating."
            className="min-h-32 rounded-2xl border-[#E5E7EB] focus-visible:ring-[#3FFFB6]"
          />

          {matches === null && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-2">
                Try an example
              </p>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPTS.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => setQuery(ex)}
                    data-testid="ai-match-example"
                    className="text-xs px-3 py-1.5 rounded-full bg-[#FAFAFA] ring-1 ring-[#F3F4F6] text-[#4B5563] hover:ring-[#B8FFDD] hover:text-[#111827] transition-colors text-left"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Button
            type="submit"
            data-testid="ai-match-submit"
            disabled={loading}
            className="mt-6 w-full h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold text-base transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Finding matches…
              </>
            ) : (
              <>
                <Sparkles size={16} className="mr-2" />
                Find my match
              </>
            )}
          </Button>
        </form>

        {matches && matches.length > 0 && (
          <div className="mt-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-4">
              Top {matches.length} {matches.length === 1 ? "match" : "matches"}
            </p>
            <ul className="space-y-4">
              {matches.map((m, i) => (
                <li key={m.personId}>
                  <Link
                    to={`/people/${m.person.id}`}
                    data-testid={`ai-match-result-${m.person.id}`}
                    onClick={() => onOpenChange(false)}
                    className="group block bg-white rounded-2xl p-5 ring-1 ring-[#F3F4F6] hover:ring-[#B8FFDD] transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <img
                          src={m.person.photo}
                          alt={m.person.name}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                        <span className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-[#3FFFB6] text-[#111827] text-xs font-bold flex items-center justify-center ring-2 ring-white">
                          {i + 1}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-display font-semibold text-[#111827] leading-tight">
                              {m.person.name}
                            </p>
                            <p className="text-sm text-[#4B5563]">
                              {m.person.role}
                            </p>
                          </div>
                          <ArrowUpRight
                            size={16}
                            className="text-[#9CA3AF] group-hover:text-[#3FFFB6] mt-1 flex-shrink-0"
                          />
                        </div>
                        <p className="mt-3 text-sm text-[#111827] leading-relaxed border-l-2 border-[#3FFFB6] pl-3">
                          {m.reason}
                        </p>
                        <p className="mt-3 text-xs font-medium text-[#065F46]">
                          {getVerifiedSolutions(m.person)}{" "}
                          {getVerifiedSolutions(m.person) === 1
                            ? "solution"
                            : "solutions"}
                        </p>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <button
              onClick={reset}
              data-testid="ai-match-refine"
              className="mt-6 w-full text-sm text-[#4B5563] hover:text-[#111827] transition-colors"
            >
              Refine your description
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
